/**
 * Storage targets and the changes-only backup (piece 9; decision 0265).
 * 🔨 One active Primary, which the proxy and every upload use; a Mirror
 * receives copies and serves nothing. 🔨 A run looks only at changes: the
 * versions with no copy row or another hash, and the copies of versions
 * whose bytes are gone. 🔨 The database is dumped whole and pruned by age.
 */
import type { StoredFilesFacts } from './encryption';
import { addCalendarDays, instantFromWallClock, wallClockInZone } from '../zonedTime';
import { hourLabel } from '../pages/calendarGrid';
import { t } from '../strings';
import { REHEARSAL_OVERDUE_DAYS } from './rehearsalDays';
import { ENVIRONMENT_KEY_PREFIXES, holdsUnprefixedKeys, keyPrefixOf, unprefixedKey, type EnvironmentKeyPrefix } from './environmentKey';

export const STORAGE_TARGET_ROLES = ['Primary', 'Mirror'] as const;
export type StorageTargetRole = (typeof STORAGE_TARGET_ROLES)[number];

/** Rehearsal joined on 2026-09-28 (decision 1196): the restore that proves the backups and the recovery copy open. RecoverPrepare joined on 2026-10-13 (decision 4040): a Prepare of Recover the database. */
export const BACKUP_RUN_KINDS = ['Mirror', 'DatabaseDump', 'Rehearsal', 'RecoverPrepare', 'Recover', 'RecoverFiles', 'RecoverUndo'] as const;
export type BackupRunKind = (typeof BACKUP_RUN_KINDS)[number];

export const BACKUP_OUTCOMES = ['Succeeded', 'Partial', 'Failed'] as const;
export type BackupOutcome = (typeof BACKUP_OUTCOMES)[number];

export const BACKUP_ENABLED_KEY = 'backup.enabled';
export const MIRROR_RUN_HOUR_KEY = 'backup.mirrorRunHour';
export const DUMP_RUN_HOUR_KEY = 'backup.databaseDumpRunHour';
export const DUMP_RETENTION_DAYS_KEY = 'backup.dumpRetentionDays';
/** Decision 4040: how long a prepared copy of Recover the database stays before it is dropped. */
export const RECOVER_PREPARE_HOURS_KEY = 'backup.recoverPrepareHours';
/** Decisions 4040, 4063: how long the database from before a recovery is kept for Undo, and its safety dump kept from the prune. */
export const RECOVER_KEEP_DAYS_KEY = 'backup.recoverKeepDays';
/** Decisions 4040, 4060: how long a switched database has to answer before the guard switches back. */
export const RECOVER_START_TIMEOUT_KEY = 'backup.recoverStartTimeoutSeconds';

/**
 * The schedule's four Organization-scoped rows, seeded here and edited on the
 * Storage and backups page, and Recover the database's three (decision 4040),
 * seeded beside them and drawn on no card.
 */
export const BACKUP_SETTINGS: readonly { key: string; value: number | boolean }[] = [
  { key: BACKUP_ENABLED_KEY, value: true },
  { key: MIRROR_RUN_HOUR_KEY, value: 2 },
  { key: DUMP_RUN_HOUR_KEY, value: 3 },
  { key: DUMP_RETENTION_DAYS_KEY, value: 30 },
  { key: RECOVER_PREPARE_HOURS_KEY, value: 4 },
  { key: RECOVER_KEEP_DAYS_KEY, value: 7 },
  { key: RECOVER_START_TIMEOUT_KEY, value: 300 },
];

/**
 * 🔒 Decision 4360: the dump key pair's public half, an Organization row Generate Key on the Dump Encryption card
 * alone writes. Nothing seeds it and no form edits it; while it is absent the dump is written in plain text.
 */
export const DUMP_PUBLIC_KEY_SETTING_KEY = 'backup.dumpPublicKey';

/** Decision 4360: Generate Key and the dump key's standing, a super admin's route on the storage controller. */
export const DUMP_KEY_PATH = '/settings/storage/dump-key';

/** 🔒 Decision 4360: an encrypted dump is the dump's own key with this added, as `age` names its files. */
export const ENCRYPTED_DUMP_SUFFIX = '.age';

/** 🔒 Decision 4360: the signature object stands beside its dump, under the dump's key with this added. */
export const DUMP_SIGNATURE_SUFFIX = '.sig';

/** What `backup.dumpPublicKey` holds: the `age` recipient, never the private half. */
export interface StoredDumpPublicKey {
  format: 'age-x25519';
  publicKey: string;
  fingerprint: string;
  generatedAt: string;
  generatedByPersonId: string;
}

/** What the Dump Encryption card reads: whether a key is set and who made it, and whether this server signs its dumps. */
export interface DumpKeyStatus {
  set: boolean;
  fingerprint: string | null;
  generatedAt: string | null;
  generatedByName: string | null;
  /** True where the server holds the host key the signature key is derived from. */
  signing: boolean;
  /** Generate Key is a super admin's act. */
  mayManage: boolean;
}

/** 🔨 Generate Key's one answer: the private half, shown once on the sheet and kept nowhere. */
export interface DumpKeySheet {
  privateKey: string;
  publicKey: string;
  fingerprint: string;
  generatedAt: string;
}

/** The object a dump's signature is written under. */
export function dumpSignatureKey(dumpKey: string): string {
  return `${dumpKey}${DUMP_SIGNATURE_SUFFIX}`;
}

/** Whether a dump's key names an encrypted dump. */
export function isEncryptedDumpKey(key: string): boolean {
  return key.endsWith(`.dump${ENCRYPTED_DUMP_SUFFIX}`);
}

/** The environment store's seeded row: the one Primary a database with no targets starts with. */
export const ENVIRONMENT_TARGET_NAME = 'This server’s store';

export interface StorageTargetInput {
  name?: string | null;
  role?: string | null;
  endpoint?: string | null;
  bucket?: string | null;
  region?: string | null;
  accessKeyId?: string | null;
  secretKey?: string | null;
  pathStyle?: boolean;
}

/** A target names its store completely; a credential is required on every row but the environment's own. */
export function targetProblem(input: StorageTargetInput, options: { requireCredential?: boolean } = {}): string | null {
  if (!input.name?.trim()) return 'a storage target carries a name; type a name for this store, so a backup can say where it went';
  if (input.role && !(STORAGE_TARGET_ROLES as readonly string[]).includes(input.role)) return `${input.role} is not a storage role`;
  const endpoint = input.endpoint?.trim() ?? '';
  if (!/^https?:\/\/[^\s/]+/.test(endpoint)) return 'the endpoint is an http or https URL';
  if (!/^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(input.bucket?.trim() ?? '')) return 'the bucket is 3 to 63 characters of lower-case letters, digits, dots and dashes';
  if (options.requireCredential !== false && (!input.accessKeyId?.trim() || !input.secretKey?.trim())) return 'an external store needs its access key and its secret; enter both, or point this target at the environment’s own store instead';
  return null;
}

export interface VersionLike {
  id: string;
  contentHash: string;
  sizeBytes: number;
  permanentlyDeletedAt?: Date | string | null;
}

export interface CopyLike {
  fileVersionId: string;
  contentHash: string;
}

/**
 * What one run does against one Mirror, and nothing more: copy the live
 * versions with no row or another hash, remove the copies of versions whose
 * bytes are gone. A copied, unchanged version is never touched again.
 */
export function mirrorPlan(versions: readonly VersionLike[], copies: readonly CopyLike[]): { toCopy: VersionLike[]; toRemove: string[] } {
  const byVersion = new Map(copies.map((c) => [c.fileVersionId, c.contentHash]));
  const gone = new Set(versions.filter((v) => v.permanentlyDeletedAt).map((v) => v.id));
  const toCopy = versions.filter((v) => !v.permanentlyDeletedAt && byVersion.get(v.id) !== v.contentHash);
  const toRemove = copies.filter((c) => gone.has(c.fileVersionId)).map((c) => c.fileVersionId);
  return { toCopy, toRemove };
}

/**
 * The dump's object key: one per run, named by the minute it started, under one prefix the prune reads.
 * 🔒 Decision 7540: the server writes it after its environment's prefix, `run/database/` on Run.
 */
export const DUMP_PREFIX = 'database/';

/** 🔒 Decision 7540: the dump folders an environment prunes and rehearses, its own and, on Run, the unprefixed one written before the prefix. */
export function ownDumpPrefixes(prefix: EnvironmentKeyPrefix): string[] {
  return [`${prefix}${DUMP_PREFIX}`, ...(holdsUnprefixedKeys(prefix) ? [DUMP_PREFIX] : [])];
}

/** 🔒 Decision 7540: every environment's dump folder, which Recover lists, so Test can recover Run's dump. */
export function everyDumpPrefix(): string[] {
  return [DUMP_PREFIX, ...Object.values(ENVIRONMENT_KEY_PREFIXES).map((prefix) => `${prefix}${DUMP_PREFIX}`)];
}

export function dumpObjectKey(now: Date): string {
  return `${DUMP_PREFIX}bms2-${now.toISOString().slice(0, 16).replace(/[-:]/g, '').replace('T', '-')}.dump`;
}

/** 🔒 Decision 4063: the safety dump Recover takes first, the dump's own key with `-before-recover` before the extension. */
export const SAFETY_DUMP_SUFFIX = '-before-recover.dump';
export function safetyDumpKey(now: Date): string {
  return dumpObjectKey(now).replace(/\.dump$/, SAFETY_DUMP_SUFFIX);
}

/** The minute a safety dump's key names, in UTC, or null for any other key. */
function safetyDumpTime(key: string): number | null {
  const m = /bms2-(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})-before-recover\.dump(?:\.age)?$/.exec(key);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5])) : null;
}

/**
 * The master key's recovery copy, beside the dumps on every Mirror (decision
 * 1195). 🔨 It is not a dump: the prune and the newest-dump read pass over it.
 */
export const RECOVERY_COPY_OBJECT_KEY = `${DUMP_PREFIX}master-key.recovery`;

/** 🔒 Decision 7540: the recovery copy beside a dump, under that dump's own prefix; a dump made before the prefix reads the unprefixed copy. */
export function recoveryCopyKeyBeside(dumpKey: string): string {
  return `${keyPrefixOf(dumpKey) ?? ''}${RECOVERY_COPY_OBJECT_KEY}`;
}

/** A dump, plain or encrypted (decision 4360); a signature and the recovery copy are not. */
function isDump(key: string): boolean {
  return unprefixedKey(key).startsWith(DUMP_PREFIX) && (key.endsWith('.dump') || isEncryptedDumpKey(key));
}

/** The newest dump a listing of the prefix holds, or null when it holds none. */
export function newestDumpKey(objects: readonly { key: string; lastModified: Date | string }[]): string | null {
  return dumpKeysNewestFirst(objects)[0] ?? null;
}

/** Every dump a listing of the prefix holds, newest first: the order the rehearsal tries them in (decision 4360). */
export function dumpKeysNewestFirst(objects: readonly { key: string; lastModified: Date | string }[]): string[] {
  return objects.filter((o) => isDump(o.key)).sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime() || b.key.localeCompare(a.key)).map((o) => o.key);
}

/**
 * The dumps past the retention window; the newest is always kept, however old, so a stalled schedule never prunes the last copy.
 * 🔒 Decision 4063: a safety dump younger than the keep days, by the minute its key names, is kept whatever the retention says.
 */
export function dumpsToPrune(dumps: readonly { key: string; lastModified: Date | string }[], retentionDays: number, now: Date, recoverKeepDays = 0): string[] {
  const sorted = dumps.filter((d) => isDump(d.key)).sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());
  const cutoff = now.getTime() - retentionDays * 86_400_000;
  const undoWindow = now.getTime() - recoverKeepDays * 86_400_000;
  const undoOpen = (key: string): boolean => {
    const at = safetyDumpTime(key);
    return at !== null && at >= undoWindow;
  };
  const pruned = sorted.slice(1).filter((d) => new Date(d.lastModified).getTime() < cutoff && !undoOpen(d.key)).map((d) => d.key);
  // Decision 4360: a pruned dump's signature goes with it, where the listing holds one.
  const held = new Set(dumps.map((d) => d.key));
  return pruned.flatMap((key) => (held.has(dumpSignatureKey(key)) ? [key, dumpSignatureKey(key)] : [key]));
}

/**
 * The wall-clock hour and the date in the Organization's zone, which the
 * schedule reads (T532). 🔨 An invalid zone now throws instead of the
 * silent UTC fallback this carried before — piece 9's Open issues names it.
 */
function localParts(now: Date, timeZone: string): { hour: number; day: string } {
  const wall = wallClockInZone(now, timeZone);
  return { hour: Number(wall.slice(11, 13)), day: wall.slice(0, 10) };
}

/** A job is due once its hour has passed today in the Organization's zone and it has not succeeded today. */
export function jobDue(lastSucceededAt: Date | string | null | undefined, runHour: number, now: Date, timeZone: string): boolean {
  const today = localParts(now, timeZone);
  if (today.hour < runHour) return false;
  if (!lastSucceededAt) return true;
  return localParts(new Date(lastSucceededAt), timeZone).day !== today.day;
}

/** Make primary is offered only on a verified Mirror that holds every live version. */
export function makePrimaryProblem(target: { role: string; isActive: boolean; lastVerifiedAt: Date | string | null | undefined; lastError?: string | null }, liveVersions: number, copiedVersions: number): string | null {
  if (target.role === 'Primary') return 'this target is already the Primary';
  if (!target.isActive) return 'an inactive target cannot become the Primary';
  if (!target.lastVerifiedAt || target.lastError) return 'test the connection first; the store has not answered';
  if (copiedVersions < liveVersions) return `${liveVersions - copiedVersions} live version(s) have no copy on this target yet; run the mirror first`;
  return null;
}

/** The outcome a finished run states: Failed when nothing could be done, Partial when anything failed, Succeeded otherwise. */
export function runOutcome(due: number, done: number, failed: number): BackupOutcome {
  if (failed === 0) return 'Succeeded';
  return done === 0 && due > 0 ? 'Failed' : 'Partial';
}

export { REHEARSAL_OVERDUE_DAYS };

export type RehearsalStanding = 'current' | 'overdue' | 'nothingToRehearse';

/**
 * Where the monthly rehearsal stands. It counts from the last rehearsal that
 * succeeded, or from the first dump that did when none has, so a rehearsal
 * that never ran turns overdue 35 days after there was a backup to rehearse.
 */
export function rehearsalStanding(lastSucceededAt: Date | string | null | undefined, firstDumpAt: Date | string | null | undefined, now: Date): { standing: RehearsalStanding; days: number | null } {
  const from = lastSucceededAt ?? firstDumpAt;
  if (!from) return { standing: 'nothingToRehearse', days: null };
  const days = Math.floor((now.getTime() - new Date(from).getTime()) / 86_400_000);
  return { standing: days > REHEARSAL_OVERDUE_DAYS ? 'overdue' : 'current', days };
}

/**
 * The Backups stepper on Storage and backups (piece 9; decision 1761): five
 * legs, each read from `StorageTarget`, `BackupRun` and the recovery copy, and
 * each naming the runbook section that does it. 🔒 Nothing here is stored.
 */
export const BACKUP_STEPS_PATH = '/settings/storage/backups';

/** Decision 4360 added the encryption leg after the dump; decision 7420 the stored files' leg last. */
export const BACKUP_STEPS = ['officeMirror', 'cloudMirror', 'dump', 'encryption', 'recoveryCopy', 'rehearsal', 'files'] as const;
export type BackupStep = (typeof BACKUP_STEPS)[number];

export type MirrorPlace = 'office' | 'cloud';

/** One Mirror leg as the stepper reads it: the target standing for the place, and its latest finished Mirror run. */
export interface BackupLegFacts {
  targetName: string;
  isActive: boolean;
  lastOutcome: BackupOutcome | null;
  lastRunAt: string | null;
}

export interface BackupStepsView {
  office: BackupLegFacts | null;
  cloud: BackupLegFacts | null;
  activeMirrors: number;
  /** The latest finished dump, and whether every active Mirror's latest finished dump Succeeded. */
  dump: { lastOutcome: BackupOutcome | null; lastRunAt: string | null; everyMirrorSucceeded: boolean };
  /** The recovery copy: whether a passphrase seals it, when, and the latest dump that Succeeded since, which wrote it beside the dump. */
  recovery: { passphraseSet: boolean; sealedAt: string | null; writtenAt: string | null };
  rehearsal: { lastSucceededAt: string | null };
  /** Decision 4360: whether the dump key's public half is set, its fingerprint, and whether this server signs; absent from an answer older than it. */
  encryption?: { publicKeySet: boolean; fingerprint: string | null; signing: boolean } | null;
  /** Decision 4180: the latest backup alert, or null where none was sent; absent from an answer older than the alert. */
  lastAlert?: BackupAlertView | null;
  /** Decision 7420: the stored files that should be encrypted and are not; absent from an answer older than it. */
  files?: StoredFilesFacts | null;
  /** Decision 7540: the prefix this server writes every object key under, `run/` or `test/`; absent from an answer older than it. */
  keyPrefix?: EnvironmentKeyPrefix;
}

const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
const OFFICE_SUFFIXES = ['.ts.net', '.local', '.lan', '.home.arpa'];

/**
 * 🔒 A Mirror's place, read off its endpoint (decision 1761). A loopback, a
 * private range, the tailnet's 100.64.0.0/10, a `.ts.net`, `.local`, `.lan` or
 * `.home.arpa` name, or a single-label host reads as the office; every other
 * host reads as the cloud. An endpoint that is no URL reads as the cloud.
 */
export function mirrorPlaceOf(endpoint: string): MirrorPlace {
  let host: string;
  try {
    host = new URL(endpoint.trim()).hostname.toLowerCase().replace(/^\[|\]$/g, '');
  } catch {
    return 'cloud';
  }
  if (host === 'localhost' || host === '::1') return 'office';
  const quad = IPV4.exec(host);
  if (quad) {
    const [a, b] = [Number(quad[1]), Number(quad[2])];
    if (a === 127 || a === 10) return 'office';
    if (a === 172 && b >= 16 && b <= 31) return 'office';
    if (a === 192 && b === 168) return 'office';
    if (a === 100 && b >= 64 && b <= 127) return 'office';
    return 'cloud';
  }
  if (!host.includes('.')) return 'office';
  return OFFICE_SUFFIXES.some((suffix) => host.endsWith(suffix)) ? 'office' : 'cloud';
}

export interface BackupStepFacts {
  done: boolean;
  reachable: boolean;
}

/**
 * 🔒 Each Backups step's done test (decision 1761). A mirror leg is done while
 * its active Mirror's latest run Succeeded; the dump while every active
 * Mirror's latest dump did; the recovery copy once a dump Succeeded since it
 * was sealed; the rehearsal while one Succeeded within 35 days; the stored
 * files while none that should be encrypted is plain (decision 7420).
 */
export function backupSteps(view: BackupStepsView, now: Date): Record<BackupStep, BackupStepFacts> {
  const legDone = (leg: BackupLegFacts | null) => leg !== null && leg.isActive && leg.lastOutcome === 'Succeeded';
  const mirrored = view.activeMirrors > 0;
  const dumpDone = mirrored && view.dump.everyMirrorSucceeded;
  const copyDone = view.recovery.passphraseSet && view.recovery.writtenAt !== null;
  const last = view.rehearsal.lastSucceededAt;
  const rehearsed = last !== null && now.getTime() - new Date(last).getTime() <= REHEARSAL_OVERDUE_DAYS * 86_400_000;
  return {
    officeMirror: { done: legDone(view.office), reachable: true },
    cloudMirror: { done: legDone(view.cloud), reachable: true },
    dump: { done: dumpDone, reachable: mirrored },
    encryption: { done: view.encryption?.publicKeySet === true, reachable: true },
    recoveryCopy: { done: copyDone, reachable: mirrored },
    rehearsal: { done: rehearsed, reachable: dumpDone && copyDone },
    files: { done: view.files ? view.files.due === 0 && view.files.mirroredSincePass !== false : false, reachable: true },
  };
}


/**
 * The backup alert (piece 9; decisions 4180, 4181). A dump or mirror run that
 * finishes Failed or Partial, and a night with no Succeeded run two hours
 * after its hour, tell every super admin by a to-do and a text. 🔒 One alert
 * per kind per night, marked by the `backup.alerted` audit row whose
 * correlation id is the kind and the night.
 */
export const BACKUP_ALERT_KINDS = ['Mirror', 'DatabaseDump'] as const;
export type BackupAlertKind = (typeof BACKUP_ALERT_KINDS)[number];

/** The `OperationFailure` the alert writes, which its to-do and its texts are filed against. */
export const BACKUP_ALERT_OPERATION = 'backup.alert';

/** 🔨 Decision 4181: the watchdog's grace, held in code so the watch never falls before its run. */
export const BACKUP_WATCH_GRACE_HOURS = 2;

/** Decision 4181: the two watchdog jobs on the clock, one per nightly run. */
export const WATCH_MIRROR_JOB = 'watchMirrorChanges';
export const WATCH_DUMP_JOB = 'watchDumpDatabase';

/** The hour the watchdog of a run hour runs, wrapping past midnight. */
export function watchHour(runHour: number): number {
  return (runHour + BACKUP_WATCH_GRACE_HOURS) % 24;
}

/**
 * 🔒 The night an instant belongs to (decision 4180): the Organization's day
 * whose run hour the instant is at or after. A run at 1 AM with the hour at 3
 * belongs to the night before.
 */
export function backupNight(at: Date, runHour: number, timeZone: string): string {
  const here = localParts(at, timeZone);
  return here.hour < runHour ? addCalendarDays(here.day, -1) : here.day;
}

/** The instant a night's run hour falls on, in the Organization's zone. */
export function nightStartsAt(night: string, runHour: number, timeZone: string): Date {
  return instantFromWallClock(`${night}T${String(runHour).padStart(2, '0')}:00`, timeZone);
}

/** The night a watchdog pass at `now` checks: the night of the instant its grace ago. */
export function watchedNight(now: Date, runHour: number, timeZone: string): string {
  return backupNight(new Date(now.getTime() - BACKUP_WATCH_GRACE_HOURS * 3_600_000), runHour, timeZone);
}

/** The `backup.alerted` row's correlation id: the kind and the night, such as `DatabaseDump:2026-10-14`. */
export function backupAlertKey(kind: BackupAlertKind, night: string): string {
  return `${kind}:${night}`;
}

/** The kind a correlation id names, or null for one that is not an alert's. */
export function backupAlertKindOf(key: string | null | undefined): BackupAlertKind | null {
  const kind = key?.split(':')[0];
  return (BACKUP_ALERT_KINDS as readonly string[]).includes(kind ?? '') ? (kind as BackupAlertKind) : null;
}

/** One finished run the alert names: its target, its outcome and its counts. */
export interface BackupAlertRun {
  targetName: string | null;
  outcome: 'Failed' | 'Partial';
  error: string | null;
  due: number;
  done: number;
  removed: number;
}

/** What one alert says: a run that finished badly, or a night with no Succeeded run since its hour. */
export type BackupAlertFacts =
  | { kind: BackupAlertKind; runs: readonly BackupAlertRun[] }
  | { kind: BackupAlertKind; missedSinceHour: number };

function sentenceEnd(text: string): string {
  const trimmed = text.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} ${t('settings.storage.alert.and')} ${names[names.length - 1]}`;
}

/** The longest reason a text carries; the to-do and the failures page keep the whole of it. */
const TEXT_REASON_LIMIT = 240;

/**
 * 🔒 The alert's words (decision 4180): a sentence naming the kind, the
 * targets and the reason, which the to-do, the audit row and the page carry,
 * and the text a super admin's phone receives.
 */
export function backupAlertWords(facts: BackupAlertFacts): { title: string; sentence: string; text: string } {
  const title = t(facts.kind === 'Mirror' ? 'settings.storage.alert.kind.mirror' : 'settings.storage.alert.kind.dump');
  let sentence: string;
  if ('missedSinceHour' in facts) {
    sentence = `${title} ${t('settings.storage.alert.missed')} ${hourLabel(facts.missedSinceHour)}${t('settings.storage.alert.missedTail')}`;
  } else {
    const failed = facts.runs.some((run) => run.outcome === 'Failed');
    const verb = t(failed ? 'settings.storage.alert.failed' : 'settings.storage.alert.partial');
    const targets = [...new Set(facts.runs.map((run) => run.targetName).filter((name): name is string => name !== null))];
    const where = targets.length > 0 ? ` ${t('settings.storage.alert.on')} ${joinNames(targets)}` : '';
    const reasons = [...new Set(facts.runs.map((run) => run.error
      ? sentenceEnd(run.error)
      : `${Math.max(0, run.due - run.done - run.removed)} ${t('settings.storage.alert.of')} ${run.due} ${t('settings.storage.alert.copiesFailed')}`))];
    sentence = `${title} ${verb}${where}: ${reasons.join(' ')}`;
  }
  const reason = sentence.length > TEXT_REASON_LIMIT ? `${sentence.slice(0, TEXT_REASON_LIMIT - 1).trimEnd()}…` : sentence;
  return { title, sentence, text: `${t('settings.storage.alert.textLead')} ${reason} ${t('settings.storage.alert.textTail')}` };
}

/** 🔒 The last alert as the Backups stepper and the run list draw it (decision 4180). */
export interface BackupAlertView {
  at: string;
  kind: BackupAlertKind;
  /** True where the watchdog raised it for a night with no Succeeded run, false where a run finished badly. */
  missed: boolean;
  sentence: string;
}
